import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';
import Select, { MenuPlacement } from 'react-select';
import styled, { css } from 'styled-components';

export const StyledSelect = styled(Select)<{ $hasError?: boolean }>`
  width: 100%;

  &.react-select--is-disabled {
    pointer-events: auto;

    .react-select__control {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
  .react-select__control {
    box-shadow: none !important;
    border-radius: 4px;
    height: 46px;

    ${({ theme, $hasError }) => css`
      border: 1px solid
        ${$hasError ? theme.colors.error : theme.colors.grayscale};
      font-family: ${theme.typography.captionSemibold.fontFamily};
      font-size: ${theme.typography.captionSemibold.fontSize};
      font-weight: ${theme.typography.captionSemibold.fontWeight};
      &:hover,
      &--is-focused {
        border-color: ${theme.colors.gray};
      }
    `}
    &--menu-is-open {
      .react-select__indicator {
        transform: rotate(180deg);
      }
    }
  }
  .react-select__indicator-separator {
    display: none;
  }
  .react-select__indicator {
    transition: 0.2s ease-out transform;
    color: ${({ theme }) => theme.colors.black};
  }
  .react-select__value-container {
    padding: 0 16px;
  }
  .react-select__single-value {
    color: ${({ theme }) => theme.colors.black};
    margin: 0;
  }

  .react-select__menu {
    border: 0;
    padding: 4px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 4px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  }

  .react-select__menu-list {
  }

  .react-select__option,
  .react-select__menu-notice {
    padding: 9px 12px;
    border: 0;
    border-radius: 4px;
    margin: 0 0 4px;
    display: flex;
    min-height: 40px;
    align-items: center;
    cursor: pointer;
    ${({ theme }) => css`
      background: ${theme.colors.white};
      font-family: ${theme.typography.captionSemibold.fontFamily};
      font-size: ${theme.typography.captionSemibold.fontSize};
      line-height: 22px;
      font-weight: ${theme.typography.captionSemibold.fontWeight};
      color: ${theme.colors.gray};
      &:hover,
      &--is-selected {
        background: ${theme.colors.grayscaleLight};
        color: ${theme.colors.black};
      }
    `}
    &:last-of-type {
      margin: 0;
    }
  }
  .react-select__placeholder {
    color: ${({ theme }) => theme.colors.gray};
  }
  .react-select__menu-notice {
    text-align: center;
    cursor: auto;
    justify-content: center;
  }
`;

export type Option<T = string | number> = {
  value: T;
  label: string;
};

interface Props {
  options: Option<string | number>[];
  control: Control<any>;
  name: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  $hasError?: boolean;
}

export const Dropdown: FC<Props> = ({
  control,
  options,
  name,
  disabled = false,
  placeholder = 'Select',
  id,
  $hasError = false,
}) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <StyledSelect
          $hasError={$hasError}
          classNamePrefix="react-select"
          isDisabled={disabled}
          options={options}
          onChange={onChange}
          value={value}
          onBlur={onBlur}
          id={id}
          placeholder={placeholder}
        />
      )}
    />
  );
};

interface NewDropdownProps<T> {
  options: Option<T>[];
  control: Control<any>;
  name: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  menuPlacement?: MenuPlacement;
}

export const NewDropdown = <T extends any>({
  control,
  options,
  name,
  disabled = false,
  placeholder = 'Select',
  id,
  menuPlacement,
}: NewDropdownProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => (
        <StyledSelect
          menuPlacement={menuPlacement}
          $hasError={!!error}
          classNamePrefix="react-select"
          isDisabled={disabled}
          options={options}
          onChange={(option) => onChange((option as Option<T>).value)}
          value={options.find((option) => option.value === value)}
          onBlur={onBlur}
          id={id}
          placeholder={placeholder}
        />
      )}
    />
  );
};
